<template>
  <v-container class="author-page">
    <v-row class="mb-6">
      <v-col
        cols="12"
        sm="4"
        md="3"
        class="d-flex flex-column align-center align-sm-start"
      >
        <v-avatar
          size="200"
          color="grey lighten-2"
        >
          <v-img
            v-if="author.imageUrl"
            :src="author.imageUrl"
            :alt="author.name"
          />
        </v-avatar>
      </v-col>

      <v-col
        cols="12"
        sm="8"
        md="9"
      >
        <h1 class="author-name mb-4 text-center text-sm-left">
          {{ author.name }}
        </h1>
        <a
          v-if="author.websiteUrl"
          :href="author.websiteUrl"
          target="_blank"
          rel="noopener"
          class="website-link d-block d-sm-inline-block text-center text-sm-left mb-4"
        >
          {{ $t('author.websiteLink') }}
        </a>
        <p class="author-bio mb-0 text-center text-sm-left">
          {{ author.bio }}
        </p>
      </v-col>
    </v-row>

    <div
      v-if="visibleSocials.length"
      class="d-flex justify-end flex-wrap mb-2 social-icons"
    >
      <a
        v-for="social in visibleSocials"
        :key="social.key"
        :href="social.url"
        target="_blank"
        rel="noopener"
        class="ml-2"
        :aria-label="social.key"
      >
        <v-icon
          size="28"
          :color="social.color"
        >
          {{ social.icon }}
        </v-icon>
      </a>
    </div>

    <v-divider class="mb-4" />

    <v-row
      no-gutters
      align="center"
      class="mb-4"
    >
      <v-spacer />
      <span class="mr-2 sort-label">{{ $t('shop.sortBy') }}:</span>
      <v-select
        v-model="sortKey"
        :items="sortOptions"
        dense
        outlined
        hide-details
        class="sort-select"
      />
    </v-row>

    <apollo-error-alert
      v-if="booksError"
      class="mt-3"
      :error="booksError"
    />
    <v-progress-circular
      v-if="booksLoading && books.length === 0"
      indeterminate
      color="primary"
      class="my-6 d-block mx-auto"
    />
    <!-- Mobile: a publisher-style responsive grid (2-up) instead of the carousel.
         Reuses the page's batched ownership data via external ownership props. -->
    <v-container
      v-else-if="books.length > 0 && $vuetify.breakpoint.xs"
      class="pa-0"
    >
      <v-row class="ma-n1">
        <book-list-item
          v-for="book in books"
          :id="book.id"
          :key="book.id"
          :book="book"
          :price="book.price"
          :thumbnail-url="book.thumbnailUrl"
          :title="book.title"
          :currency="book.currency"
          :format="book.format"
          :vat-included="book.vatIncluded || false"
          :show-buy-now="false"
          elevation="4"
          use-external-ownership
          :external-owned-book="checkInBookshelf(book.id)"
          :external-bookshelf-loading="bookshelfLoading"
          :external-refetch-bookshelf="refetchBookshelf"
        />
      </v-row>
    </v-container>
    <v-slide-group
      v-else-if="books.length > 0"
      v-model="activeIndex"
      :center-active="shouldRotate"
      show-arrows="always"
      class="book-carousel border--default rounded-lg pa-2"
      @mouseenter.native="pauseAutoplay"
      @mouseleave.native="resumeAutoplay"
    >
      <v-slide-item
        v-for="(book, idx) in carouselBooks"
        :key="`${book.id}-${idx}`"
        class="ma-2"
      >
        <book-list-item
          :id="book.id"
          :price="book.price"
          :thumbnail-url="book.thumbnailUrl"
          :title="book.title"
          :currency="book.currency"
          :format="book.format"
          :vat-included="book.vatIncluded || false"
          :show-buy-now="false"
          :stack-buy-now-buttons="true"
          column-classes=""
          class="book-carousel-item pa-0"
          :elevation="$vuetify.breakpoint.xs ? '16' : '4'"
          use-external-ownership
          :external-owned-book="checkInBookshelf(book.id)"
          :external-bookshelf-loading="bookshelfLoading"
          :external-refetch-bookshelf="refetchBookshelf"
        />
      </v-slide-item>
    </v-slide-group>
    <v-container v-else-if="!booksLoading">
      <v-row justify="center">
        <v-col
          class="col-12 col-sm-8 pa-16"
          justify="center"
          align="center"
        >
          <book-list-icon :size="72" />
          <h1>{{ $t('author.noBooks') }}</h1>
          <v-btn
            tile
            block
            color="primary"
            class="mt-4"
            :to="{ name: 'home' }"
          >
            {{ $t('common.returnToShop') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import BookListItem from "@/components/bookList/BookListItem";
import BookListIcon from "@/components/icons/BookListIcon";
import ApolloErrorAlert from "@/components/ApolloErrorAlert";
import LocalBookFragments from "@/graphql/LocalBookFragments";
import AuthorQueries from "@/graphql/AuthorQueries";
import { resolveSocialUrl } from "@/helpers/socialHandleHelper";
import BookshelfMixin from "@/mixins/BookshelfMixin";
import { createIri } from "@/iris";
import gql from "graphql-tag";

// Auto-rotate only when the author has more than this many books; with fewer the
// carousel is shown statically (rotating a handful of covers just reads as a
// flicker, not a feature).
const MIN_BOOKS_TO_ROTATE = 4;
const AUTOPLAY_INTERVAL_MS = 3000;

const LOCAL_BOOK_CORE_FRAGMENT = LocalBookFragments.localBookCoreFragment;

// SORT_KEYS values double as the i18n key suffix under author.sort.* so the labels
// reuse the existing translations.
const SORT_KEYS = Object.freeze({
  NEWEST: "newestToOldest",
  OLDEST: "oldestToNewest",
  PRICE_LOW: "priceLowToHigh",
  PRICE_HIGH: "priceHighToLow",
  TITLE_AZ: "titleAToZ",
  TITLE_ZA: "titleZToA",
});

// Backend OrderFilter on LocalBook exposes title, price, publisher_name,
// format_mimeType and publishedOn — newest/oldest map to publishedOn server-side.
const SORT_TO_ORDER_INPUT = Object.freeze({
  [SORT_KEYS.NEWEST]: [{ publishedOn: "DESC" }],
  [SORT_KEYS.OLDEST]: [{ publishedOn: "ASC" }],
  [SORT_KEYS.PRICE_LOW]: [{ price: "ASC" }],
  [SORT_KEYS.PRICE_HIGH]: [{ price: "DESC" }],
  [SORT_KEYS.TITLE_AZ]: [{ title: "ASC" }],
  [SORT_KEYS.TITLE_ZA]: [{ title: "DESC" }],
});

const GET_AUTHOR_BOOKS = gql`
  query getAuthorBooks(
    $authorId: String!
    $order: [LocalBookFilter_order]
  ) {
    localBooks(
      bookAuthors_author_id: $authorId
      order: $order
      itemsPerPage: 60
    ) {
      collection {
        ...LocalBookCore
      }
    }
  }
  ${LOCAL_BOOK_CORE_FRAGMENT}
`;

const EMPTY_AUTHOR = {
  name: "",
  bio: "",
  imageUrl: null,
  websiteUrl: null,
  twitterHandle: null,
  instagramHandle: null,
  tiktokHandle: null,
  facebookHandle: null,
  youtubeHandle: null,
};

export default {
  name: "AuthorPage",
  components: {
    BookListItem,
    BookListIcon,
    ApolloErrorAlert,
  },
  mixins: [BookshelfMixin],
  data() {
    return {
      sortKey: SORT_KEYS.NEWEST,
      author: { ...EMPTY_AUTHOR },
      authorLoading: false,
      books: [],
      booksLoading: false,
      booksError: null,
      activeIndex: 0,
      autoplayTimer: null,
    };
  },
  created() {
    this.fetchAuthor();
    this.fetchBooks();
  },
  beforeDestroy() {
    this.stopAutoplay();
  },
  watch: {
    "$route.params.id"() {
      this.fetchAuthor();
      this.fetchBooks();
    },
    sortKey() {
      this.fetchBooks();
    },
    shouldRotate() {
      this.restartAutoplay();
    },
  },
  computed: {
    authorId() {
      return this.$route.params.id;
    },
    orderInput() {
      return SORT_TO_ORDER_INPUT[this.sortKey] || null;
    },
    sortOptions() {
      return Object.values(SORT_KEYS).map((key) => ({
        text: this.$t(`author.sort.${key}`),
        value: key,
      }));
    },
    visibleSocials() {
      const map = [
        { key: "twitter", field: "twitterHandle", icon: "mdi-twitter", color: "#000000" },
        { key: "instagram", field: "instagramHandle", icon: "mdi-instagram", color: "#E1306C" },
        { key: "tiktok", field: "tiktokHandle", icon: "mdi-music-note", color: "#000000" },
        { key: "facebook", field: "facebookHandle", icon: "mdi-facebook", color: "#1877F2" },
        { key: "youtube", field: "youtubeHandle", icon: "mdi-youtube", color: "#FF0000" },
      ];
      return map
        .map((s) => ({ ...s, url: resolveSocialUrl(s.key, this.author[s.field]) }))
        .filter((s) => s.url);
    },
    shouldRotate() {
      return this.books.length > MIN_BOOKS_TO_ROTATE;
    },
    // Each book appears once — no padding/repeats. Autoplay advances through the
    // covers and wraps back to the first when it reaches the end.
    carouselBooks() {
      return this.books;
    },
    // Override BookshelfMixin's default so a single batched getMyOwnedBooks covers
    // every author book. Each rendered (and 4x-repeated) book-list-item then reads
    // ownership from this shared result via use-external-ownership instead of
    // firing its own query.
    bookshelfProductIds() {
      return this.books.map((book) => book.id);
    },
  },
  methods: {
    async fetchAuthor() {
      if (!this.authorId) return;
      this.authorLoading = true;
      try {
        const result = await this.$apollo.query({
          query: AuthorQueries.getAuthor,
          variables: { id: createIri("author", this.authorId) },
          fetchPolicy: "cache-first",
        });
        const fetched = result.data?.author;
        if (fetched) {
          this.author = { ...EMPTY_AUTHOR, ...fetched };
        }
      } catch (error) {
        console.error("Error fetching author:", error);
      } finally {
        this.authorLoading = false;
      }
    },
    async fetchBooks() {
      if (!this.authorId) return;
      this.booksLoading = true;
      this.booksError = null;
      try {
        const result = await this.$apollo.query({
          query: GET_AUTHOR_BOOKS,
          variables: {
            authorId: this.authorId,
            order: this.orderInput,
          },
          fetchPolicy: "cache-first",
        });
        const collection = result.data?.localBooks?.collection || [];
        // The author↔book join can repeat a book when an author holds more than one
        // role on it (e.g. author + illustrator), so dedupe by book id.
        const seen = new Set();
        this.books = collection.filter((book) => {
          if (seen.has(book.id)) return false;
          seen.add(book.id);
          return true;
        });
        this.activeIndex = 0;
        this.restartAutoplay();
      } catch (error) {
        this.booksError = error;
        console.error("Error fetching author books:", error);
      } finally {
        this.booksLoading = false;
      }
    },
    startAutoplay() {
      if (!this.shouldRotate || this.autoplayTimer) return;
      this.autoplayTimer = setInterval(() => {
        const len = this.carouselBooks.length;
        if (len === 0) return;
        this.activeIndex = (this.activeIndex + 1) % len;
      }, AUTOPLAY_INTERVAL_MS);
    },
    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    },
    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    },
    pauseAutoplay() {
      this.stopAutoplay();
    },
    resumeAutoplay() {
      this.startAutoplay();
    },
  },
};
</script>

<style scoped>
.author-name {
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1.1;
}
.website-link {
  color: #1976d2;
  text-decoration: none;
}
.website-link:hover {
  text-decoration: underline;
}
.author-bio {
  font-size: 1rem;
  line-height: 1.6;
}
.sort-label {
  font-size: 0.9rem;
}
.sort-select {
  max-width: 200px;
}
.social-icons a {
  text-decoration: none;
}
/* Stretch every slide item to the tallest card so tiles are a uniform height,
   regardless of title length or whether a book shows buy-now buttons. */
.book-carousel ::v-deep .v-slide-group__content {
  align-items: stretch;
}
/* book-list-item's root is a v-col; pin its width and let it fill the stretched
   slide item (its inner v-card already uses fill-height). */
/* Match the home page book tiles, which render at ~180px wide (col-lg-2) on
   desktop and ~150px (col-6, 2-up) on mobile. */
.book-carousel-item {
  /* book-list-item's root is a v-col (.col → flex: 1 1 0). Inside the slide-group
     flex row that grow would override the fixed width, so a single book stretches
     to fill the whole carousel. Pin flex so the 180px width is always honored. */
  flex: 0 0 auto;
  width: 180px;
  height: 100%;
}
@media (max-width: 599.98px) {
  .book-carousel-item {
    width: 150px;
  }
}
</style>
